import { Injectable, signal } from '@angular/core';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import { ConfigService } from '../../features/config/services/config.service';

interface StockThresholdResponse {
  lowStockThreshold: number;
  lowSampleStockThreshold: number;
}

@Injectable({
  providedIn: 'root',
})
export class StockThresholdService {
  readonly lowStockThreshold = signal<number>(10);
  readonly lowSampleStockThreshold = signal<number>(5);

  private initializePromise: Promise<void> | null = null;
  private initialized = false;

  constructor(private readonly configService: ConfigService) {}

  initialize(): Promise<void> {
    if (this.initialized) {
      return Promise.resolve();
    }

    if (this.initializePromise) {
      return this.initializePromise;
    }

    this.initializePromise = firstValueFrom(
      this.configService.getByStockThreshold().pipe(
        map((response: StockThresholdResponse) => {
          return {
            lowStockThreshold: this.toNumber(response?.lowStockThreshold, this.lowStockThreshold()),
            lowSampleStockThreshold: this.toNumber(
              response?.lowSampleStockThreshold,
              this.lowSampleStockThreshold()
            ),
          };
        }),
        map((thresholds) => {
          this.lowStockThreshold.set(thresholds.lowStockThreshold);
          this.lowSampleStockThreshold.set(thresholds.lowSampleStockThreshold);
          this.initialized = true;
        }),
        catchError(() => {
          this.initialized = true;
          return of(undefined);
        })
      )
    )
      .then(() => undefined)
      .finally(() => {
        this.initializePromise = null;
      });

    return this.initializePromise;
  }

  private toNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
}
